import type { LimitKey } from '@/types/api'

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const

/** Formats a byte count using binary multiples (1 KB = 1024 B), e.g. `5368709120` → `5 GB`. */
export function formatBytes(bytes: number | null | undefined, fractionDigits = 1): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—'
  if (bytes === 0) return '0 B'
  const negative = bytes < 0
  let value = Math.abs(bytes)
  let unit = 0
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024
    unit++
  }
  const digits = unit === 0 ? 0 : fractionDigits
  const rounded = Number(value.toFixed(digits))
  return `${negative ? '-' : ''}${rounded.toLocaleString('en-US', { maximumFractionDigits: digits })} ${BYTE_UNITS[unit]}`
}

export const UNLIMITED = 'Unlimited'

const BYTE_LIMITS: ReadonlySet<LimitKey> = new Set(['storage_bytes', 'upload_bytes'])

export function isByteLimit(key: LimitKey): boolean {
  return BYTE_LIMITS.has(key)
}

/** Formats a plan limit value. `null` means unlimited. */
export function formatLimit(key: LimitKey, value: number | null | undefined): string {
  if (value === null) return UNLIMITED
  if (value === undefined) return '—'
  if (isByteLimit(key)) return formatBytes(value)
  if (key === 'analytics_retention_days') return `${formatNumber(value)} ${value === 1 ? 'day' : 'days'}`
  return formatNumber(value)
}

export const LIMIT_LABELS: Record<LimitKey, string> = {
  displays: 'Displays',
  locations: 'Locations',
  players: 'Players',
  users: 'Team members',
  storage_bytes: 'Storage',
  upload_bytes: 'Max upload size',
  analytics_retention_days: 'Analytics retention',
}

/** Ratio used for usage meters: 0..1, or `null` when unlimited. */
export function usageRatio(used: number, limit: number | null | undefined): number | null {
  if (limit === null || limit === undefined) return null
  if (limit <= 0) return used > 0 ? 1 : 0
  return Math.min(1, Math.max(0, used / limit))
}

export type UsageTone = 'ok' | 'warning' | 'critical'

export function usageTone(ratio: number | null): UsageTone {
  if (ratio === null) return 'ok'
  if (ratio >= 0.95) return 'critical'
  if (ratio >= 0.8) return 'warning'
  return 'ok'
}

export function formatUsage(key: LimitKey, used: number, limit: number | null): string {
  const fmt = (n: number) => (isByteLimit(key) ? formatBytes(n) : formatNumber(n))
  return `${fmt(used)} / ${limit === null ? UNLIMITED : fmt(limit)}`
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

export function formatMoney(cents: number, currency = 'USD'): string {
  const value = cents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(undefined, opts).format(date)
}

export function formatDateTime(iso: string | null | undefined): string {
  return formatDate(iso, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * Absolute date-time in a specific IANA zone with its offset, e.g. `Oct 7, 2026, 9:42:10 AM GMT+2`
 * (device-local time for a display). Falls back to the browser zone for unknown zones.
 */
export function formatDateTimeInZone(iso: string | null | undefined, zone: string | null | undefined, opts: { seconds?: boolean } = {}): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const base: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(opts.seconds ? { second: '2-digit' } : {}),
    timeZoneName: 'shortOffset',
  }
  try {
    return new Intl.DateTimeFormat('en-US', { ...base, timeZone: zone ?? undefined }).format(date)
  } catch {
    return new Intl.DateTimeFormat('en-US', base).format(date)
  }
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
]

export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const diff = (date.getTime() - now.getTime()) / 1000
  const abs = Math.abs(diff)
  if (abs < 45) return 'just now'
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  for (const [unit, seconds] of RELATIVE_UNITS) {
    if (abs >= seconds || unit === 'second') return rtf.format(Math.round(diff / seconds), unit)
  }
  return formatDate(iso)
}

/** `location.created` → `Location created`; `users.role_changed` → `Users role changed`. */
export function humanizeAction(action: string): string {
  const text = action.replace(/[._-]+/g, ' ').trim()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function humanize(key: string): string {
  return humanizeAction(key)
}
